import { ColumnsType } from "antd/es/table";
import { COLOR_TYPE, PropertyData, UserData } from "./CampaignReport.type";
import { Tag } from "Components";
import { PDF_URL } from "PhishingManager/service/index.constant";
import { capitalize } from "PhishingManager/utils";

export const STATUS_COLOR: Record<string, COLOR_TYPE> = {
    high: { color: '#006CA3', bg: '#E6F7FD' },
    medium: { bg: "#FFF7E0", color: "#000000" },
    low: { bg: "#FBEAEA", color: "#D32F2F" },
};

export const RESULT_COLOR: Record<string, COLOR_TYPE> = {
    Passed: { color: '#006CA3', bg: '#E6F7FD' },
    Failed: { bg: "#FBEAEA", color: "#D32F2F" },
};

export const INTERACTION_COLOR: COLOR_TYPE = { color: '#636766', bg: '#EAEAEB' };

export const GET_COLOR_BY_SCORE = (score: number) => {
    const status = score >= 94 ? "high" : (score < 94 && score >= 90) ? "medium" : "low"
    const { bg, color } = STATUS_COLOR[status];
    return {
        bg, color,
        tag: score >= 97 ? "Excellent" : (score < 97 && score >= 94)  ? "Good" : (score < 94 && score >= 90) ? "Fair" : "Poor"
    }
}

export const TABLE_TABS = ["individual", "property"];

const renderStatusTag = (value: number, color: string, bg: string) => {
    return value ? <Tag className='status-tag' style={{ color, background: bg }}>{value}</Tag> : "-"
};

const renderPercentTag = (value: number) => {
    const { color, bg } = INTERACTION_COLOR;
    return <Tag className='status-tag' style={{ color, background: bg }}>{`${value}%`}</Tag>
};

export const INDIVIDUAL_TABLE_COLUMNS: ColumnsType<UserData> = [
    {
        title: "Name",
        key: "name",
        render: (_, record) => `${record.first_name || ""} ${record.last_name || ""}`.trim(),
    },
    {
        title: "Email Address",
        dataIndex: "email",
        key: "email",
        responsive: ['lg', 'xl', 'xxl'],
    },
    {
        title: "Property",
        dataIndex: "properties",
        key: "properties",
        render: (value) => value?.length > 1 ? value?.length : capitalize(value?.[0] || '-')
    },
    {
        title: "Group",
        dataIndex: "groups",
        key: "groups",
        render: (value) => value?.length > 1 ? value?.length : capitalize(value?.[0] || '-')
    },
    {
        title: "Source",
        dataIndex: "source",
        key: "source",
        render: (result, record) => {
            const value = record.email_delivery === "No" ? 0 : result;
            return value || "-";
        }
    },
    {
        title: "Result",
        dataIndex: "result",
        key: "result",
        align: "center",
        // Optional: Color code Passed vs Failed
        render: (result, record) => {
            const { color, bg } = RESULT_COLOR[result] || RESULT_COLOR.Failed;
            const value = record.email_delivery === "No" ? 0 : result;
            return renderStatusTag(value, color, bg);
        }
    },
    {
        title: "Interaction",
        dataIndex: "interaction",
        key: "interaction",
        align: "center",
        render: (interaction, record) => {
            const { color, bg } = INTERACTION_COLOR;
            const value = record.email_delivery === "No" ? 0 : interaction;
            return renderStatusTag(value, color, bg);
        }
    },
]

export const PROPERTY_TABLE_COLUMNS: ColumnsType<PropertyData> = [
    {
        title: "Property Name",
        dataIndex: "group_name",
        key: "group_name",
    },
    {
        title: "Pass Rate",
        dataIndex: "pass_rate",
        key: "pass_rate",
        align: "center",
        render: (value: number) => renderPercentTag(value),
    },
    {
        title: "Open Rate",
        dataIndex: "opened_rate",
        key: "opened_rate",
        align: "center",
        render: (value: number) => renderPercentTag(value),
    },
    {
        title: "Click Rate",
        dataIndex: "clicked_rate",
        key: "clicked_rate",
        align: "center",
        render: (value: number) => renderPercentTag(value),
    },
    {
        title: "Submission Rate",
        dataIndex: "submission_rate",
        key: "submission_rate",
        align: "center",
        render: (value: number) => renderPercentTag(value),
        responsive: ['lg', 'xl', 'xxl'],
    },
    {
        title: "Emails Sent",
        dataIndex: "email_sent_count",
        key: "email_sent_count",
    },
    {
        title: "Opened Email",
        dataIndex: "email_opened_count",
        key: "email_opened_count",
        responsive: ['lg', 'xl', 'xxl'],
    },
    {
        title: "Clicked Link",
        dataIndex: "clicked_link_count",
        key: "clicked_link_count",
        responsive: ['lg', 'xl', 'xxl'],
    },
    {
        title: "Submitted Data",
        dataIndex: "submitted_data_count",
        key: "submitted_data_count",
        responsive: ['lg', 'xl', 'xxl'],
    },
    {
        title: "Total Users",
        dataIndex: "users_count",
        key: "users_count",
    }
];

export const INTERACTION_PDF_URL =  `${PDF_URL}/learn-more-pages/interpreting_results.pdf`;
export const AUTOMATION_PDF_URL = `${PDF_URL}/learn-more-pages/automation_detection.pdf`;